/**
 * Stalwart Prints — global theme behaviour.
 *
 * Deliberately small and dependency-free. Native platform features do the
 * heavy lifting: <dialog> handles modality, focus trapping and Escape;
 * <details> handles mobile submenu disclosure. This file only covers what the
 * platform does not.
 *
 * Nothing here is required for the page to be usable — navigation links work
 * without JavaScript, and the mobile menu falls back to a plain link list.
 */

(() => {
  'use strict';

  /* ----------------------------------------------------------------------
   * Dialogs and drawers
   * Any element with [data-dialog-open="<id>"] opens that dialog.
   * Any element with [data-dialog-close] closes its containing dialog.
   * -------------------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('data-dialog-open'));
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        dialog.showModal();
        opener.setAttribute('aria-expanded', 'true');
        dialog.dispatchEvent(new CustomEvent('dialog:opened', { bubbles: true }));
      }
      return;
    }

    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) {
        event.preventDefault();
        dialog.close();
      }
      return;
    }

    // Clicking the backdrop closes the dialog. A click on the backdrop lands on
    // the dialog element itself rather than any of its children.
    if (event.target.matches('dialog[open]')) {
      event.target.close();
    }
  });

  // Keep openers' aria-expanded honest however the dialog was closed.
  document.addEventListener(
    'close',
    (event) => {
      if (!(event.target instanceof HTMLDialogElement)) return;
      const id = event.target.id;
      if (!id) return;
      document
        .querySelectorAll(`[data-dialog-open="${id}"]`)
        .forEach((opener) => opener.setAttribute('aria-expanded', 'false'));
    },
    true
  );

  /* ----------------------------------------------------------------------
   * Desktop navigation dropdowns
   * Progressive enhancement over a plain nested link list.
   * -------------------------------------------------------------------- */

  class NavDropdown extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-dropdown-trigger]');
      this.panel = this.querySelector('[data-dropdown-panel]');
      if (!this.trigger || !this.panel) return;

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);

      this.trigger.addEventListener('click', this.onTriggerClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    get open() {
      return this.trigger.getAttribute('aria-expanded') === 'true';
    }

    set open(value) {
      this.trigger.setAttribute('aria-expanded', value ? 'true' : 'false');
      this.panel.hidden = !value;
    }

    onTriggerClick(event) {
      event.preventDefault();
      const wasOpen = this.open;
      closeAllDropdowns();
      this.open = !wasOpen;
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.open) {
        this.open = false;
        this.trigger.focus();
      }
    }

    onFocusOut(event) {
      if (!this.contains(event.relatedTarget)) this.open = false;
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.open = false;
    }
  }

  function closeAllDropdowns() {
    document.querySelectorAll('nav-dropdown').forEach((el) => {
      if (el.trigger) el.open = false;
    });
  }

  /* ----------------------------------------------------------------------
   * Announcement bar
   * Two layouts, chosen by the merchant.
   *
   * Stepper: one message at a time with explicit controls, never rotating.
   *
   * Marquee: a ticker. It is still until this script starts it, and the
   * script starts it only when it can stop it again: a pause button that is
   * remembered for the session, a stop when reduced motion is asked for, and
   * a hold while a keyboard user is on one of its links or the merchant has a
   * message selected in the theme editor. The merchant's Motion setting is
   * applied in Liquid, which leaves out data-marquee-speed when it is None.
   * -------------------------------------------------------------------- */

  const MARQUEE_PAUSED_KEY = 'announcement-paused';

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.setupDismiss();
      if (this.dataset.layout === 'marquee') {
        this.setupMarquee();
      } else {
        this.setupStepper();
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.reducedMotion) this.reducedMotion.removeEventListener('change', this.layoutMarquee);
    }

    setupStepper() {
      this.items = Array.from(this.querySelectorAll('[data-announcement]'));
      this.status = this.querySelector('[data-announcement-status]');
      if (this.items.length < 2) return;

      this.index = 0;
      this.querySelectorAll('[data-announcement-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const step = Number(button.getAttribute('data-announcement-step'));
          this.show(this.index + step);
        });
      });
      this.show(0);
    }

    /**
     * Dismissal is remembered for the browsing session only. sessionStorage is
     * unavailable in some privacy modes, so every access is guarded and the bar
     * simply stays visible if storage throws.
     */
    setupDismiss() {
      const key = this.dataset.dismissible;
      if (!key) return;

      try {
        if (sessionStorage.getItem(key) === 'dismissed') {
          this.hidden = true;
          return;
        }
      } catch (error) {
        /* storage blocked — show the bar */
      }

      const button = this.querySelector('[data-announcement-dismiss]');
      if (!button) return;

      button.addEventListener('click', () => {
        this.hidden = true;
        try {
          sessionStorage.setItem(key, 'dismissed');
        } catch (error) {
          /* storage blocked — dismissal lasts for this page only */
        }
      });
    }

    show(next) {
      const total = this.items.length;
      this.index = (next + total) % total;
      this.items.forEach((item, i) => {
        item.hidden = i !== this.index;
      });
      if (this.status) {
        this.status.textContent = this.status.dataset.template
          .replace('__CURRENT__', String(this.index + 1))
          .replace('__TOTAL__', String(total));
      }
    }

    setupMarquee() {
      this.speed = Number(this.dataset.marqueeSpeed);
      this.viewport = this.querySelector('[data-marquee-viewport]');
      this.track = this.querySelector('[data-marquee-track]');
      this.group = this.querySelector('[data-marquee-group]');
      this.toggle = this.querySelector('[data-marquee-toggle]');
      if (!this.speed || !this.viewport || !this.track || !this.group || !this.toggle) return;

      this.toggleLabel = this.toggle.querySelector('[data-marquee-toggle-label]');
      this.holds = new Set();
      this.layoutMarquee = this.layoutMarquee.bind(this);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', this.layoutMarquee);

      let paused = false;
      try {
        paused = sessionStorage.getItem(MARQUEE_PAUSED_KEY) === 'true';
      } catch (error) {
        /* storage blocked — start playing */
      }
      this.setPaused(paused);

      this.toggle.addEventListener('click', () => {
        const next = !this.hasAttribute('data-paused');
        this.setPaused(next);
        try {
          sessionStorage.setItem(MARQUEE_PAUSED_KEY, String(next));
        } catch (error) {
          /* storage blocked — the choice lasts for this page only */
        }
      });

      // A link the keyboard lands on has to be on screen and holding still.
      // Pointer focus is left alone, or a click would reflow the bar just
      // before the page navigates.
      this.viewport.addEventListener('focusin', (event) => {
        if (event.target.matches(':focus-visible')) this.hold('focus', true);
      });
      this.viewport.addEventListener('focusout', (event) => {
        if (!this.viewport.contains(event.relatedTarget)) this.hold('focus', false);
      });

      // A moving target is hard to select in the theme editor.
      this.addEventListener('shopify:block:select', () => this.hold('editor', true));
      this.addEventListener('shopify:block:deselect', () => this.hold('editor', false));

      // Widths change with the window and again once the brand font arrives.
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.layoutMarquee();
          });
        });
        this.observer.observe(this.viewport);
        this.observer.observe(this.group);
      }
      this.layoutMarquee();
    }

    hold(reason, on) {
      if (on) this.holds.add(reason);
      else this.holds.delete(reason);
      this.layoutMarquee();
    }

    setPaused(paused) {
      this.toggleAttribute('data-paused', paused);
      this.toggleLabel.textContent = paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause;
    }

    layoutMarquee() {
      const reduce = this.reducedMotion.matches;
      this.toggle.hidden = reduce;

      if (reduce || this.holds.size > 0) {
        this.stopMarquee();
        return;
      }

      // The group is measured in its one-line ticker layout.
      this.setAttribute('data-marquee', '');
      const distance = this.group.getBoundingClientRect().width;
      const view = this.viewport.clientWidth;
      if (!distance || !view) {
        this.stopMarquee();
        return;
      }

      // Enough copies that the viewport is still full once the track has
      // moved by one copy's width and loops back.
      this.setCopies(Math.ceil(view / distance));
      this.style.setProperty('--marquee-distance', `${distance}px`);
      this.style.setProperty('--marquee-duration', `${(distance / this.speed).toFixed(2)}s`);
    }

    stopMarquee() {
      this.removeAttribute('data-marquee');
      this.setCopies(0);
    }

    setCopies(count) {
      const copies = this.track.querySelectorAll('[data-marquee-copy]');
      if (copies.length === count) return;
      copies.forEach((copy) => copy.remove());

      for (let i = 0; i < count; i += 1) {
        const copy = this.group.cloneNode(true);
        copy.removeAttribute('data-marquee-group');
        copy.setAttribute('data-marquee-copy', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.inert = true;
        copy.querySelectorAll('a').forEach((link) => {
          link.tabIndex = -1;
        });
        // Only the real message should answer to the theme editor.
        copy.querySelectorAll('[data-shopify-editor-block]').forEach((item) => {
          item.removeAttribute('data-shopify-editor-block');
        });
        this.track.append(copy);
      }
    }
  }

  /* ----------------------------------------------------------------------
   * Scroll rows
   * The track scrolls natively and snaps without any of this. This only adds
   * previous and next buttons, shown when there is something to scroll to.
   * -------------------------------------------------------------------- */

  class ScrollRow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-scroll-track]');
      this.controls = this.querySelector('[data-scroll-controls]');
      if (!this.track || !this.controls) return;

      this.buttons = Array.from(this.controls.querySelectorAll('[data-scroll-step]'));
      this.update = this.update.bind(this);
      this.onScroll = this.onScroll.bind(this);

      this.controls.addEventListener('click', (event) => {
        const button = event.target.closest('[data-scroll-step]');
        if (!button || button.getAttribute('aria-disabled') === 'true') return;
        this.step(Number(button.dataset.scrollStep));
      });
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      // In the theme editor, selecting a block should bring it into view
      // rather than leave the merchant hunting for it off-screen.
      this.addEventListener('shopify:block:select', (event) => {
        event.target.scrollIntoView({ block: 'nearest', inline: 'start' });
      });

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.update);
        this.observer.observe(this.track);
      }
      this.update();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    /** Scrolls by one screenful. Snapping settles it on an item edge. */
    step(direction) {
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({
        left: this.track.clientWidth * 0.9 * direction * (rtl ? -1 : 1),
        behavior: reduce ? 'auto' : 'smooth',
      });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.controls.hidden = max <= 1;
      if (max <= 1) return;

      // scrollLeft runs negative in right-to-left layouts.
      const position = Math.abs(this.track.scrollLeft);
      this.buttons.forEach((button) => {
        const atEnd = Number(button.dataset.scrollStep) < 0 ? position <= 1 : position >= max - 1;
        button.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
      });
    }
  }

  /* ----------------------------------------------------------------------
   * Reveal on first view
   * An element marked [data-reveal] is finished by default. Only when this
   * script can also complete the reveal does it set the "pending" state, and
   * it moves to "done" the first time the element is on screen. So without
   * JavaScript, without IntersectionObserver, or with reduced motion asked
   * for, nothing is ever left waiting to appear.
   * -------------------------------------------------------------------- */

  function initReveals() {
    const targets = document.querySelectorAll('[data-reveal]:not([data-reveal-state])');
    if (targets.length === 0) return;
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute('data-reveal-state', 'done');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.35 }
    );

    targets.forEach((target) => {
      target.setAttribute('data-reveal-state', 'pending');
      observer.observe(target);
    });
  }

  initReveals();
  // The theme editor re-renders a section in place when it is edited.
  document.addEventListener('shopify:section:load', initReveals);

  /* ----------------------------------------------------------------------
   * Auto-submitting form controls
   * The collection toolbar is a real <form> and works without JavaScript via
   * its noscript Apply button. This only removes the extra click.
   * -------------------------------------------------------------------- */

  document.addEventListener('change', (event) => {
    const control = event.target.closest('[data-auto-submit]');
    if (control && control.form) control.form.requestSubmit();
  });

  /* ----------------------------------------------------------------------
   * Money
   * The receipt multiplies prices, so it formats them itself with the shop's
   * money format, which arrives on the receipt element. The same rules as the
   * design editor's formatter (customizer/editor/pricing.ts); the two cannot
   * share code across the theme / app boundary.
   * -------------------------------------------------------------------- */

  function formatMoney(cents, format) {
    const groups = (n, decimals, thousands, point) => {
      const fixed = (n / 100).toFixed(decimals);
      const [whole, fraction] = fixed.split('.');
      const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${withThousands}${point}${fraction}` : withThousands;
    };
    const text = String(format || '{{amount}}').replace(/<[^>]*>/g, '');
    return text.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return groups(cents, 0, ',', '.');
        case 'amount_with_comma_separator':
          return groups(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return groups(cents, 0, '.', ',');
        default:
          return groups(cents, 2, ',', '.');
      }
    });
  }

  /* ----------------------------------------------------------------------
   * Product page: options, quantity, sizes, receipt and the add button
   *
   * One controller, because they all answer one question: what exactly is
   * about to go in the cart, and for how much. Prices arrive from Liquid; this
   * never decides a price, it only multiplies what the variants already say.
   *
   * Without JavaScript the radios and the quantity field still submit as a
   * normal form and Shopify resolves the variant server-side.
   *
   * A customizable product must not reach the cart without a design, or there
   * is nothing to print, so the button waits for the editor's saved event and
   * says so in words. The editor is a separate application (a theme app
   * extension); the theme listens for its events and reads one attribute, and
   * knows nothing else about it.
   * -------------------------------------------------------------------- */

  // Set by initProductPage: the lines the add button would put in the cart, as
  // [{ id, quantity }]. Read by the print-charge submit below. Null on pages
  // without the product form, where the form's own id and quantity apply.
  let productOrderLines = null;
  let productIsMultiSize = () => false;

  function initProductPage() {
    const form = document.getElementById('product-form');
    const dataEl = document.querySelector('[data-variant-data]');
    if (!form || !dataEl) return;

    let variants = [];
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (error) {
      return; // Malformed payload: leave the server-rendered state alone.
    }
    if (!Array.isArray(variants) || variants.length === 0) return;

    const addButton = form.querySelector('[data-add-to-cart]');
    const addLabel = addButton?.querySelector('[data-add-label]');
    const idInput = form.querySelector('[data-variant-id]');
    const priceEl = document.querySelector('[data-price]');
    const receipt = document.querySelector('[data-receipt]');
    const receiptStatus = document.querySelector('[data-receipt-status]');
    const qtyRoot = document.querySelector('[data-qty]');
    const qtyInput = document.querySelector('[data-qty-input]');
    const single = qtyRoot?.querySelector('[data-qty-single]');
    const multi = qtyRoot?.querySelector('[data-qty-multi]');
    const sizePosition = Number(qtyRoot?.dataset.sizePosition) || 0;
    const bar = document.querySelector('[data-buy-bar]');
    const barTotal = bar?.querySelector('[data-buy-bar-total]');
    const barDesign = bar?.querySelector('[data-buy-bar-design]');
    const barAdd = bar?.querySelector('[data-buy-bar-add]');

    const text = {
      add: addButton?.dataset.addText || addLabel?.textContent.trim() || 'Add to cart',
      soldOut: addButton?.dataset.soldOutText || 'Sold out',
      unavailable: addButton?.dataset.unavailableText || 'Unavailable',
      chooseSizes: addButton?.dataset.chooseSizesText || 'Enter how many of each size',
      needsDesign: addButton?.dataset.needsDesignText || 'Add your design to continue',
    };
    const moneyFormat = receipt?.dataset.moneyFormat || '{{amount}}';
    const money = (cents) => formatMoney(cents, moneyFormat);
    const pieces = (count) =>
      count === 1
        ? receipt?.dataset.piecesOne || '1 piece'
        : (receipt?.dataset.piecesOther || '%COUNT% pieces').replace('%COUNT%', String(count));

    // The hold, and every "add your design" prompt, only make sense when the
    // design editor is actually on the page. A product can have customization
    // switched on and still have no editor: the app block failed to render (the
    // app is not installed on this store, or the template's block belongs to
    // another store's copy of the app), or the product has no customizer
    // configuration and the block shows a notice instead. Holding Add to cart
    // then leaves a product nobody can buy. So without an editor it sells like
    // any other product, and the design step and printing line go away.
    const editorRoot = document.getElementById('customizer-root');
    const editorPresent = Boolean(editorRoot && editorRoot.hasAttribute('data-customizer'));
    const designRequired = Boolean(addButton?.hasAttribute('data-needs-design')) && editorPresent;
    if (addButton?.hasAttribute('data-needs-design') && !editorPresent) {
      document.querySelectorAll('[data-step-needs-editor]').forEach((step) => {
        step.hidden = true;
      });
      receipt?.querySelector('[data-receipt-print-row]')?.setAttribute('hidden', '');
      barDesign?.remove();

      // Theme editor only: say why, when the block rendered nothing at all. The
      // block explains its own notice states itself.
      const anchor = document.getElementById('customizer');
      const note = anchor?.dataset.missingEditorNote;
      if (note && !editorRoot) {
        const p = document.createElement('p');
        p.className = 'product__editor-missing';
        p.textContent = note;
        anchor.prepend(p);
      }
    }

    const state = {
      variant: variants.find((v) => String(v.id) === String(idInput?.value)) || variants[0],
      designReady: editorRoot?.dataset.designReady === 'true',
      print: null, // { units, unitPriceCents } for one piece, from the editor
      mode: 'single',
    };

    /* -- options ---------------------------------------------------------- */

    const optionInputs = () => Array.from(document.querySelectorAll('[data-variant-option]'));
    const optionCount = variants[0].options.length;

    // The selected value per option index, or null where nothing is chosen.
    function chosen() {
      const values = new Array(optionCount).fill(null);
      optionInputs().forEach((input) => {
        if (input.checked) values[Number(input.dataset.variantOption) - 1] = input.value;
      });
      return values;
    }

    function findVariant(values) {
      return variants.find((variant) => values.every((value, i) => value === null || variant.options[i] === value)) || null;
    }

    // Marks values that cannot be bought with the other current selections.
    // Marked rather than disabled, so the customer can still pass through one
    // on the way to a combination that exists.
    function markAvailability() {
      const values = chosen();
      optionInputs().forEach((input) => {
        const index = Number(input.dataset.variantOption) - 1;
        const available = variants.some(
          (variant) =>
            variant.available &&
            variant.options.every((option, j) => {
              if (j === index) return option === input.value;
              if (state.mode === 'multi' && j === sizePosition - 1) return true;
              return values[j] === null || option === values[j];
            })
        );
        input.toggleAttribute('data-unavailable', !available);
      });
    }

    function renderPrice(variant) {
      if (!priceEl || !variant) return;
      priceEl.textContent = variant.price;
      if (variant.compareAtPrice) {
        const s = document.createElement('s');
        s.className = 'product__price-compare';
        s.textContent = variant.compareAtPrice;
        priceEl.appendChild(s);
        const sale = document.createElement('span');
        sale.className = 'badge badge--sale';
        sale.textContent = priceEl.dataset.saleText || '';
        if (sale.textContent) priceEl.appendChild(sale);
      }
    }
    // Keep the sale badge's wording from the server render for later rewrites.
    if (priceEl) priceEl.dataset.saleText = priceEl.querySelector('.badge--sale')?.textContent.trim() || '';

    document.addEventListener('change', (event) => {
      const input = event.target.closest('[data-variant-option]');
      if (!input) return;

      const values = chosen();
      document.querySelectorAll('[data-option-value]').forEach((el) => {
        const value = values[Number(el.dataset.optionValue)];
        if (value) el.textContent = value;
      });

      const exact = variants.find((variant) => values.every((value, i) => variant.options[i] === value)) || null;
      state.variant = exact;
      if (exact) {
        if (idInput) idInput.value = exact.id;
        renderPrice(exact);
        // Keep the URL shareable and the back button honest.
        if (exact.url && window.history.replaceState) window.history.replaceState({}, '', exact.url);
        if (exact.mediaId) {
          document.dispatchEvent(new CustomEvent('stalwart:variant-media', { detail: { mediaId: exact.mediaId } }));
        }
      }
      markAvailability();
      render();
    });

    /* -- quantity and sizes ----------------------------------------------- */

    const clampInt = (value, min) => {
      const n = Math.floor(Number(value));
      if (!Number.isFinite(n)) return min;
      return Math.min(9999, Math.max(min, n));
    };

    qtyRoot?.addEventListener('click', (event) => {
      const step = event.target.closest('[data-qty-step]');
      if (!step || !qtyInput) return;
      qtyInput.value = String(clampInt(Number(qtyInput.value || 0) + Number(step.dataset.qtyStep), 1));
      render();
    });

    qtyInput?.addEventListener('input', render);
    qtyInput?.addEventListener('change', () => {
      qtyInput.value = String(clampInt(qtyInput.value, 1));
      render();
    });

    const sizeInputs = () => Array.from(multi?.querySelectorAll('[data-size-qty]') || []);
    const sizeFieldset = sizePosition
      ? document.querySelector(`[data-variant-option="${sizePosition}"]`)?.closest('fieldset')
      : null;

    qtyRoot?.addEventListener('change', (event) => {
      const mode = event.target.closest('[data-qty-mode]');
      if (mode) {
        state.mode = mode.value === 'multi' ? 'multi' : 'single';
        if (single) single.hidden = state.mode === 'multi';
        if (multi) multi.hidden = state.mode !== 'multi';
        // Excluded from the form while sizes are counted one by one.
        if (qtyInput) qtyInput.disabled = state.mode === 'multi';
        // In "several sizes" the size picker above would be a second, silent
        // answer to the same question, so it steps aside.
        if (sizeFieldset) {
          sizeFieldset.hidden = state.mode === 'multi';
          // A product whose only option is size would leave step 1 as a heading
          // over nothing, so the picker and its step heading step aside too.
          const picker = sizeFieldset.closest('.variant-picker');
          if (picker) {
            const empty = Array.from(picker.querySelectorAll('fieldset')).every((f) => f.hidden);
            picker.hidden = empty;
            const heading = picker.previousElementSibling;
            if (heading?.classList.contains('buy-step')) heading.hidden = empty;
          }
        }
        if (state.mode === 'multi') {
          // Start from what they had: the chosen size, with the chosen quantity.
          const inputs = sizeInputs();
          const current = chosen()[sizePosition - 1];
          if (inputs.every((input) => clampInt(input.value, 0) === 0)) {
            const match = inputs.find((input) => input.dataset.sizeQty === current);
            if (match && !match.disabled) match.value = String(clampInt(qtyInput?.value, 1));
          }
        }
        markAvailability();
        render();
        return;
      }
      const size = event.target.closest('[data-size-qty]');
      if (size) {
        size.value = String(clampInt(size.value, 0));
        render();
      }
    });
    multi?.addEventListener('input', (event) => {
      if (event.target.closest('[data-size-qty]')) render();
    });

    // The cart lines for the current selection: [{ id, quantity, variant }].
    function lines() {
      if (state.mode !== 'multi') {
        if (!state.variant) return [];
        return [{ id: state.variant.id, quantity: clampInt(qtyInput?.value ?? 1, 1), variant: state.variant }];
      }
      const values = chosen();
      const out = [];
      sizeInputs().forEach((input) => {
        const quantity = input.disabled ? 0 : clampInt(input.value, 0);
        if (!quantity) return;
        const withSize = values.slice();
        withSize[sizePosition - 1] = input.dataset.sizeQty;
        const variant = findVariant(withSize);
        if (variant && variant.available) out.push({ id: variant.id, quantity, variant });
      });
      return out;
    }

    // Sizes that don't exist, or are sold out, in the current colour/fabric.
    function syncSizeGrid() {
      const values = chosen();
      sizeInputs().forEach((input) => {
        const withSize = values.slice();
        withSize[sizePosition - 1] = input.dataset.sizeQty;
        const variant = findVariant(withSize);
        const usable = Boolean(variant && variant.available);
        input.disabled = !usable;
        if (!usable) input.value = '0';
        const cell = input.closest('.size-grid__cell');
        cell?.toggleAttribute('data-filled', usable && clampInt(input.value, 0) > 0);
        const out = cell?.querySelector('[data-size-out]');
        if (out) out.hidden = usable;
      });
    }

    productOrderLines = () => lines().map(({ id, quantity }) => ({ id, quantity }));
    productIsMultiSize = () => state.mode === 'multi';

    /* -- the receipt, the button and the bar ------------------------------ */

    let lastAnnounced = '';

    function render() {
      if (state.mode === 'multi') syncSizeGrid();
      const current = lines();
      const count = current.reduce((sum, line) => sum + line.quantity, 0);
      const itemCents = current.reduce((sum, line) => sum + line.variant.priceCents * line.quantity, 0);
      const prices = new Set(current.map((line) => line.variant.priceCents));

      // Printing is pending until a design is saved; after that it is the
      // editor's per-piece charge, or included when the product has none.
      const designed = designRequired && state.designReady;
      const printing = designed && state.print ? state.print : null;
      const printEach = printing ? printing.units * printing.unitPriceCents : 0;
      const printCents = printEach * count;
      const pending = designRequired && !designed;
      const total = pending
        ? (receipt?.dataset.totalPending || '%TOTAL% + printing').replace('%TOTAL%', money(itemCents))
        : money(itemCents + printCents);

      if (receipt) {
        const itemQty = receipt.querySelector('[data-receipt-item-qty]');
        const item = receipt.querySelector('[data-receipt-item]');
        if (itemQty) {
          itemQty.textContent =
            count > 0 && prices.size === 1 ? `${count} × ${money(current[0].variant.priceCents)}` : pieces(count);
        }
        if (item) item.textContent = money(itemCents);

        const printRow = receipt.querySelector('[data-receipt-print-row]');
        if (printRow && !printRow.hidden) {
          const printQty = printRow.querySelector('[data-receipt-print-qty]');
          const printAmount = printRow.querySelector('[data-receipt-print]');
          if (printQty) printQty.textContent = printing && count > 0 ? `${count} × ${money(printEach)}` : '';
          if (printAmount) {
            printAmount.textContent = pending
              ? receipt.dataset.printingPending || ''
              : printing
                ? money(printCents)
                : receipt.dataset.printingIncluded || '';
            printAmount.classList.toggle('receipt__amount--pending', pending);
          }
        }
        const totalEl = receipt.querySelector('[data-receipt-total]');
        if (totalEl) totalEl.textContent = total;
      }
      if (barTotal) barTotal.textContent = total;

      // Announced once it settles, not on every keystroke in a size box.
      if (receiptStatus && total !== lastAnnounced) {
        lastAnnounced = total;
        window.clearTimeout(render.announceTimer);
        render.announceTimer = window.setTimeout(() => {
          receiptStatus.textContent = total;
        }, 600);
      }

      // The button says what is missing, in words, never by greying alone.
      let label = text.add;
      let disabled = false;
      if (state.mode === 'single' && !state.variant) {
        label = text.unavailable;
        disabled = true;
      } else if (state.mode === 'single' && !state.variant.available) {
        label = text.soldOut;
        disabled = true;
      } else if (state.mode === 'multi' && count === 0) {
        label = text.chooseSizes;
        disabled = true;
      } else if (designRequired && !state.designReady) {
        label = text.needsDesign;
        disabled = true;
      }
      if (addButton) {
        addButton.disabled = disabled;
        addButton.setAttribute('aria-disabled', String(disabled));
      }
      if (addLabel) addLabel.textContent = label;

      if (barDesign && barAdd) {
        const needsDesign = designRequired && !state.designReady;
        barDesign.hidden = !needsDesign;
        barAdd.hidden = needsDesign;
      }
      if (barAdd) barAdd.disabled = disabled;
    }

    document.addEventListener('stalwart:design-saved', (event) => {
      state.designReady = true;
      const print = event.detail?.print;
      state.print =
        print && Number.isFinite(print.units) && Number.isFinite(print.unitPriceCents)
          ? { units: print.units, unitPriceCents: print.unitPriceCents }
          : null;
      render();
    });

    // Editing after saving means the design in the form is no longer the design
    // on screen, and the print files in it are the previous ones. Adding that to
    // the cart would ship something the customer had already changed, so the
    // button waits for the new save rather than attaching the old design.
    document.addEventListener('stalwart:design-dirty', () => {
      state.designReady = false;
      render();
    });

    /* -- sticky bar (phones) ---------------------------------------------- */

    // Shown while neither the add button nor the design launcher is on screen,
    // so the next action is always one tap away and never shown twice.
    if (bar && 'IntersectionObserver' in window) {
      const watched = [addButton, document.querySelector('[data-launcher]')].filter(Boolean);
      const visible = new Set();
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        bar.hidden = visible.size > 0;
      });
      watched.forEach((target) => observer.observe(target));
    }

    markAvailability();
    render();
  }

  initProductPage();

  /* ----------------------------------------------------------------------
   * Product gallery: the proof sheet
   *
   * Scroll-snap does the moving; this keeps the counter and thumbnails in step,
   * shows a variant's own photograph when it is chosen, and puts the
   * customer's saved design in front as "Your design".
   * -------------------------------------------------------------------- */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slides = this.querySelector('[data-gallery-slides]');
      if (!this.slides) return;
      this.status = this.querySelector('[data-gallery-status]');
      this.active = 0;

      this.onThumb = (event) => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (thumb) this.show(thumb.dataset.galleryThumb);
      };
      this.addEventListener('click', this.onThumb);

      let frame = 0;
      this.onScroll = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const width = this.slides.clientWidth || 1;
          this.setActive(Math.round(Math.abs(this.slides.scrollLeft) / width));
        });
      };
      this.slides.addEventListener('scroll', this.onScroll, { passive: true });

      this.onKey = (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const next = this.active + (event.key === 'ArrowRight' ? 1 : -1);
        const items = this.items();
        if (items[next]) this.show(items[next].dataset.mediaId, true);
      };
      this.slides.addEventListener('keydown', this.onKey);

      this.onVariantMedia = (event) => this.show(String(event.detail?.mediaId ?? ''));
      this.onSaved = (event) => this.addProof(event.detail?.previewUrl);
      this.onDirty = () => this.markStale(true);
      document.addEventListener('stalwart:variant-media', this.onVariantMedia);
      document.addEventListener('stalwart:design-saved', this.onSaved);
      document.addEventListener('stalwart:design-dirty', this.onDirty);
    }

    disconnectedCallback() {
      document.removeEventListener('stalwart:variant-media', this.onVariantMedia);
      document.removeEventListener('stalwart:design-saved', this.onSaved);
      document.removeEventListener('stalwart:design-dirty', this.onDirty);
    }

    items() {
      return Array.from(this.slides.children);
    }

    show(mediaId, announce = false) {
      const index = this.items().findIndex((slide) => slide.dataset.mediaId === mediaId);
      if (index < 0) return;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.slides.scrollTo({ left: index * this.slides.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      this.setActive(index);
      if (announce && this.status) {
        const img = this.items()[index].querySelector('img');
        this.status.textContent = img?.alt || '';
      }
    }

    setActive(index) {
      const items = this.items();
      if (!items.length) return;
      this.active = Math.max(0, Math.min(items.length - 1, index));
      const counter = this.querySelector('[data-gallery-counter]');
      if (counter) counter.textContent = `${this.active + 1} / ${items.length}`;
      const id = items[this.active].dataset.mediaId;
      this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
        if (thumb.dataset.galleryThumb === id) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }

    // Counter and thumbnails exist only for more than one image, so a product
    // with one photograph gains them when the proof becomes a second slide.
    ensureNavigation() {
      const count = this.items().length;
      if (count < 2) return;
      const sheet = this.querySelector('.gallery__sheet');
      if (sheet && !this.querySelector('[data-gallery-counter]')) {
        const counter = document.createElement('p');
        counter.className = 'gallery__counter';
        counter.setAttribute('data-gallery-counter', '');
        counter.setAttribute('aria-hidden', 'true');
        sheet.appendChild(counter);
      }
      let thumbs = this.querySelector('[data-gallery-thumbs]');
      if (!thumbs) {
        thumbs = document.createElement('div');
        thumbs.className = 'gallery__thumbs';
        thumbs.setAttribute('data-gallery-thumbs', '');
        sheet?.after(thumbs);
        this.items().forEach((slide) => {
          if (slide.dataset.mediaId === 'proof') return;
          const img = slide.querySelector('img');
          thumbs.appendChild(this.thumb(slide.dataset.mediaId, img?.currentSrc || img?.src || ''));
        });
      }
      // Labels count from one, in order, including the proof.
      const label = this.dataset.showLabel || 'Show image %INDEX% of %COUNT%';
      thumbs.querySelectorAll('[data-gallery-thumb]').forEach((thumb, i) => {
        thumb.setAttribute('aria-label', label.replace('%INDEX%', String(i + 1)).replace('%COUNT%', String(count)));
      });
    }

    thumb(id, src) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'gallery__thumb';
      button.dataset.galleryThumb = id;
      if (src) {
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.width = 80;
        img.height = 80;
        button.appendChild(img);
      }
      return button;
    }

    addProof(url) {
      if (!url || !/^https:\/\//.test(url)) return;
      const label = this.dataset.proofLabel || 'Your design';
      let slide = this.items().find((item) => item.dataset.mediaId === 'proof');
      if (!slide) {
        slide = document.createElement('li');
        slide.className = 'gallery__slide gallery__slide--proof';
        slide.dataset.mediaId = 'proof';
        const img = document.createElement('img');
        img.alt = label;
        img.decoding = 'async';
        const tag = document.createElement('p');
        tag.className = 'gallery__proof-tag';
        tag.setAttribute('aria-hidden', 'true');
        slide.append(img, tag);
        this.slides.prepend(slide);
      }
      slide.querySelector('img').src = url;
      this.markStale(false);

      this.ensureNavigation();
      const thumbs = this.querySelector('[data-gallery-thumbs]');
      if (thumbs) {
        let thumb = thumbs.querySelector('[data-gallery-thumb="proof"]');
        if (!thumb) {
          thumb = this.thumb('proof', url);
          thumbs.prepend(thumb);
          this.ensureNavigation();
        } else {
          thumb.querySelector('img')?.setAttribute('src', url);
        }
      }
      this.slides.scrollLeft = 0;
      this.setActive(0);
      if (this.status) this.status.textContent = label;
    }

    markStale(stale) {
      const slide = this.items().find((item) => item.dataset.mediaId === 'proof');
      if (!slide) return;
      slide.classList.toggle('gallery__slide--stale', stale);
      const tag = slide.querySelector('.gallery__proof-tag');
      if (tag) tag.textContent = stale ? this.dataset.proofStale || '' : this.dataset.proofLabel || '';
    }
  }

  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ----------------------------------------------------------------------
   * Product recommendations
   * Fetched as a rendered section when the empty element nears the viewport,
   * so they never hold up the page. No result, or any failure, leaves the
   * element empty and out of the way.
   * -------------------------------------------------------------------- */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded || !this.dataset.url) return;
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async load() {
      this.dataset.loaded = 'true';
      try {
        const response = await fetch(this.dataset.url);
        if (!response.ok) throw new Error(`recommendations ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = html.querySelector('product-recommendations');
        if (fresh && fresh.querySelector('li')) {
          this.innerHTML = fresh.innerHTML;
        } else {
          this.hidden = true;
        }
      } catch (error) {
        this.hidden = true;
      }
    }
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }

  /* ----------------------------------------------------------------------
   * Dialogs (size chart)
   * Native <dialog>: focus is moved in and returned by the browser, and Escape
   * closes it. A press on the backdrop closes it too.
   * -------------------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const opener = event.target.closest?.('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen);
      if (dialog && typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
      return;
    }
    const closer = event.target.closest?.('[data-dialog-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }
    if (event.target instanceof HTMLDialogElement && event.target.classList.contains('sheet-dialog')) {
      event.target.close();
    }
  });

  /* ----------------------------------------------------------------------
   * Shirt and print charge, added together
   *
   * A design with print area adds two lines in ONE request: the shirt, and the
   * print-area product with quantity = units x shirts. Shopify prices both; the
   * browser only decides the quantity. Shopify rejects an unknown variant for
   * the whole request, but can add FEWER than asked when stock is short, so the
   * add is verified against the cart afterwards and undone if anything is off.
   * The cart page then holds checkout if a printed shirt has no print line.
   * All of this is browser-side; the order check in the handbook is the backstop.
   * -------------------------------------------------------------------- */

  function showAddError(form, message) {
    let note = form.querySelector('[data-add-error-note]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'field__error';
      note.setAttribute('role', 'alert');
      note.setAttribute('data-add-error-note', '');
      form.appendChild(note);
    }
    // Clear first, then set on the next frame, so an identical repeat message
    // is announced again by the live region.
    note.textContent = '';
    requestAnimationFrame(() => {
      note.textContent = message;
    });
  }

  // Form fields that must not be copied onto the shirt item: the cart API takes
  // these separately or not at all.
  const PRINT_CHARGE_SKIPPED_FIELDS = new Set([
    'id', 'quantity', 'form_type', 'utf8', 'product-id', 'section-id',
    'sections', 'sections_url', 'add',
  ]);

  // The garment lines to add, [{ id, quantity }] with string ids: one per size
  // in "several sizes", otherwise the form's own variant and quantity. Null
  // when anything is malformed, so nothing half-valid reaches the cart.
  function orderLines(data) {
    const raw = productOrderLines
      ? productOrderLines()
      : [{ id: data.get('id'), quantity: parseInt(String(data.get('quantity') ?? ''), 10) || 1 }];
    const lines = raw.map((line) => ({ id: String(line.id ?? '').trim(), quantity: Number(line.quantity) }));
    if (!lines.length) return null;
    const valid = lines.every(
      (line) => /^\d+$/.test(line.id) && Number.isSafeInteger(line.quantity) && line.quantity >= 1
    );
    return valid ? lines : null;
  }

  // Returns { body } for the cart request, or { error: true } when the form
  // carries a print charge that cannot be added safely.
  function buildPrintChargeRequest(form, button) {
    const data = new FormData(form);
    const unitsRaw = String(data.get('properties[_print_units]') ?? '').trim();
    const printVariant = form.dataset.printVariantId;
    const designId = String(data.get('properties[_design_id]') ?? '').trim();

    const shirts = orderLines(data);
    if (!/^\d+$/.test(unitsRaw) || !/^\d+$/.test(printVariant || '') || !shirts || !designId) {
      return { error: true };
    }
    const units = parseInt(unitsRaw, 10);
    // Several sizes are several shirt lines with one design, and ONE print line
    // for all of them: units x every shirt.
    const pieces = shirts.reduce((sum, line) => sum + line.quantity, 0);
    const printQuantity = units * pieces;
    if (!Number.isSafeInteger(printQuantity) || printQuantity < 1) return { error: true };

    const properties = {};
    const extras = {};
    for (const [key, value] of data.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match) {
        properties[match[1]] = String(value);
      } else if (key && !key.startsWith('properties[') && !PRINT_CHARGE_SKIPPED_FIELDS.has(key) && typeof value === 'string') {
        extras[key] = value; // e.g. selling_plan, forwarded as-is
      }
    }
    const label = button?.dataset.printAreaLabel || 'Print area';

    return {
      designId,
      body: {
        items: [
          ...shirts.map((line) => ({ id: line.id, quantity: line.quantity, properties, ...extras })),
          {
            id: String(printVariant),
            quantity: printQuantity,
            properties: { _design_id: designId, [label]: properties._print_label || '' },
          },
        ],
      },
    };
  }

  // Verifying an add by re-reading the cart, not by trusting the add response.
  //
  // The same saved design added in two colours gives two shirt lines with one
  // `_design_id`, and their print lines have identical properties, so Shopify
  // merges them into ONE print line. Whether /cart/add.js then reports the
  // quantity just added or the merged line's total is unconfirmed, so comparing
  // the response to the request could roll back a good add or pass a short one.
  // Instead: snapshot every line before the add, read the cart again after it,
  // and require each requested (variant, design id) group to have grown by
  // exactly the requested quantity.

  async function readCart(root) {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    const cart = await response.json();
    if (!cart || !Array.isArray(cart.items)) throw new Error('cart.js: no items');
    return cart;
  }

  // key -> { quantity, group } for every line in the cart, where group is
  // "<variant id>|<design id>" (design id empty for lines without one).
  function snapshotLines(cart) {
    const lines = new Map();
    for (const line of cart.items) {
      const designId = line.properties && line.properties._design_id != null ? String(line.properties._design_id) : '';
      lines.set(line.key, {
        quantity: Number(line.quantity) || 0,
        group: `${String(line.variant_id)}|${designId}`,
      });
    }
    return lines;
  }

  function groupTotals(lines) {
    const totals = new Map();
    for (const { quantity, group } of lines.values()) totals.set(group, (totals.get(group) || 0) + quantity);
    return totals;
  }

  // True only when every requested (variant, design id) group holds exactly
  // its snapshot quantity plus what was requested. Shopify can add fewer than
  // asked (stock) and report it only in a message, so a short add fails here.
  function addVerified(before, after, requested, designId) {
    const want = new Map();
    for (const item of requested) {
      const group = `${String(item.id)}|${designId}`;
      want.set(group, (want.get(group) || 0) + item.quantity);
    }
    const had = groupTotals(before);
    const has = groupTotals(after);
    for (const [group, quantity] of want) {
      if ((has.get(group) || 0) !== (had.get(group) || 0) + quantity) return false;
    }
    return true;
  }

  // Puts the cart back exactly as the snapshot had it: lines present before go
  // back to their old quantity, lines the add created go to zero. Earlier adds
  // of the same design (another colour) are therefore kept.
  async function restoreSnapshot(root, before, after) {
    const updates = {};
    for (const [key, line] of after) {
      const target = before.has(key) ? before.get(key).quantity : 0;
      if (line.quantity !== target) updates[key] = target;
    }
    if (!Object.keys(updates).length) return;
    try {
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error(`update.js ${response.status}`);
    } catch (error) {
      console.warn('Could not roll back a partial add to cart', error);
    }
  }

  // Where a successful add goes. cart.js, once loaded, opens the cart drawer and
  // resolves true. Without it (script missing, the drawer switched off in Theme
  // settings, or it could not refresh) the customer lands on the cart page, as
  // they always did.
  async function goToCart(root, button) {
    try {
      if (window.StalwartCartUI && (await window.StalwartCartUI.afterAdd())) {
        button?.removeAttribute('data-loading');
        return;
      }
    } catch (error) {
      // Fall through to the cart page.
    }
    window.location.href = `${root}cart`;
  }

  // Several sizes of a product with no print charge: one request, one line per
  // size, all with the same properties (a design, if there is one). Shopify
  // rejects the whole request if any line fails, so there is nothing to undo.
  async function addPlainLines(form) {
    const button = form.querySelector('[data-add-to-cart]');
    if (button?.getAttribute('data-loading') === 'true') return;
    const fallback = button?.dataset.addError || '';
    const data = new FormData(form);
    const lines = orderLines(data);
    if (!lines) {
      showAddError(form, fallback);
      return;
    }
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match) properties[match[1]] = String(value);
    }
    const root = window.Shopify?.routes?.root || '/';
    if (button) button.setAttribute('data-loading', 'true');
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines.map((line) => ({ ...line, properties })) }),
      });
      if (response.ok) {
        goToCart(root, button);
        return;
      }
      const detail = await response.json().catch(() => null);
      if (button) button.removeAttribute('data-loading');
      showAddError(form, response.status === 422 && typeof detail?.description === 'string' ? detail.description : fallback);
    } catch (error) {
      if (button) button.removeAttribute('data-loading');
      showAddError(form, fallback);
    }
  }

  function initPrintChargeSubmit() {
    const form = document.getElementById('product-form');
    if (!form) return;

    // After Back from /cart the page can return from bfcache with the button
    // still marked loading; clear it. Registered once, not per submit.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        form.querySelector('[data-add-to-cart]')?.removeAttribute('data-loading');
      }
    });

    form.addEventListener('submit', async (event) => {
      const unitsField = form.querySelector('[name="properties[_print_units]"]');
      const unitsText = String(unitsField?.value ?? '').trim();
      // Ordinary product (or no print area): normal form post, unless several
      // sizes are being ordered, which a form post cannot express. Anything
      // else, including a malformed value, is handled (and blocked) below.
      if (!unitsText || /^0+$/.test(unitsText)) {
        if (!productIsMultiSize()) return;
        event.preventDefault();
        addPlainLines(form);
        return;
      }

      event.preventDefault();
      const button = form.querySelector('[data-add-to-cart]');
      if (button?.getAttribute('data-loading') === 'true') return;

      const fallback = button?.dataset.addError || '';
      const request = buildPrintChargeRequest(form, button);
      if (request.error) {
        showAddError(form, fallback);
        return;
      }

      const root = window.Shopify?.routes?.root || '/';
      const note = form.querySelector('[data-add-error-note]');
      if (note) note.textContent = '';
      if (button) button.setAttribute('data-loading', 'true');
      const fail = (message) => {
        if (button) button.removeAttribute('data-loading');
        showAddError(form, message || fallback);
      };

      // Without a snapshot the add could not be checked or undone, so it is
      // not attempted.
      let before;
      try {
        before = snapshotLines(await readCart(root));
      } catch (error) {
        fail('');
        return;
      }

      let response = null;
      let detail = null;
      try {
        response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(request.body),
        });
        detail = await response.json().catch(() => null);
      } catch (error) {
        response = null; // network error: the add may or may not have landed
      }

      // The cart is read back whether the request said ok or not.
      let after = null;
      for (let attempt = 0; attempt < 2 && !after; attempt += 1) {
        try {
          after = snapshotLines(await readCart(root));
        } catch (error) {
          after = null;
        }
      }
      if (!after) {
        // Cannot verify or undo. An accepted add goes to the cart, whose
        // pairing pass and missing-print check guard checkout; otherwise the
        // customer is told it failed.
        if (response?.ok) {
          goToCart(root, button);
          return;
        }
        fail('');
        return;
      }

      if (response?.ok && addVerified(before, after, request.body.items, request.designId)) {
        goToCart(root, button);
        return;
      }
      // Failed or partial: nothing of this add may stay in the cart, or the
      // customer is charged for fewer print units than they ordered.
      await restoreSnapshot(root, before, after);
      // Only a 422 with a description (stock messages) is shown as-is.
      const stockMessage =
        response?.status === 422 && detail && typeof detail.description === 'string' ? detail.description : '';
      fail(stockMessage);
    });
  }

  initPrintChargeSubmit();

  /* ----------------------------------------------------------------------
   * Search panel
   * Progressive enhancement over a link to the search page and a plain GET
   * form. The inside of the panel is rendered by Shopify from
   * sections/predictive-search.liquid: the products shown before typing are
   * fetched the first time the panel opens, then suggestions for whatever the
   * shopper types. Each answer is kept for the visit, so deleting a letter
   * shows the earlier results at once instead of asking again.
   * -------------------------------------------------------------------- */

  const SEARCH_MIN_CHARS = 2;
  const SEARCH_DEBOUNCE_MS = 250;
  const SEARCH_SLOW_MS = 800;
  const RECENT_SEARCHES_KEY = 'stalwart:recent-searches';
  const RECENT_SEARCHES_MAX = 6;

  class SearchSheet extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="q"]');
      this.status = this.querySelector('[data-search-status]');
      this.idle = this.querySelector('[data-search-idle]');
      this.results = this.querySelector('[data-search-results]');
      this.error = this.querySelector('[data-search-error]');
      this.featured = this.querySelector('[data-search-featured]');
      this.recent = this.querySelector('[data-recent]');
      if (!this.dialog || !this.form || !this.input || !this.results) return;

      this.root = window.Shopify?.routes?.root || '/';
      this.cache = new Map();
      this.controller = null;
      this.timer = 0;
      this.slowTimer = 0;
      this.featuredState = 'none';

      this.onShortcut = this.onShortcut.bind(this);
      document.addEventListener('keydown', this.onShortcut);

      this.input.addEventListener('input', () => this.onInput());
      this.dialog.addEventListener('dialog:opened', () => this.onOpen());
      this.dialog.addEventListener('close', () => this.cancel());
      this.form.addEventListener('submit', () => this.remember(this.query()));
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onShortcut);
      this.cancel();
    }

    query() {
      return this.input.value.trim();
    }

    open() {
      if (this.dialog.open) return;
      this.dialog.showModal();
      this.dialog.dispatchEvent(new CustomEvent('dialog:opened', { bubbles: true }));
    }

    onOpen() {
      this.renderRecent();
      this.loadFeatured();
      this.input.focus();
      this.input.select();
      if (this.query().length >= SEARCH_MIN_CHARS) {
        this.search();
      } else {
        this.showIdle();
      }
    }

    // "/" opens search from anywhere on the page, unless the shopper is typing
    // somewhere or another dialog is already open.
    onShortcut(event) {
      if (event.key !== '/' || event.defaultPrevented) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target;
      if (target instanceof Element && target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])')) return;
      if (document.querySelector('dialog[open]')) return;
      event.preventDefault();
      const opener = document.querySelector(`[data-dialog-open="${this.dialog.id}"]`);
      if (opener) {
        opener.click();
      } else {
        this.open();
      }
    }

    onInput() {
      window.clearTimeout(this.timer);
      const query = this.query();
      if (query.length < SEARCH_MIN_CHARS) {
        this.showIdle();
        return;
      }
      if (this.cache.has(query.toLowerCase())) {
        this.search();
        return;
      }
      this.timer = window.setTimeout(() => this.search(), SEARCH_DEBOUNCE_MS);
    }

    async search() {
      const query = this.query();
      if (query.length < SEARCH_MIN_CHARS) {
        this.showIdle();
        return;
      }

      const key = query.toLowerCase();
      if (this.cache.has(key)) {
        this.cancel();
        this.render(this.cache.get(key));
        return;
      }

      this.cancel();
      const controller = new AbortController();
      this.controller = controller;
      this.dataset.state = 'loading';
      this.results.setAttribute('aria-busy', 'true');
      // Only worth saying out loud when it is slow; otherwise the result count
      // follows straight away.
      this.slowTimer = window.setTimeout(() => {
        if (this.status) this.status.textContent = this.status.dataset.loadingText || '';
      }, SEARCH_SLOW_MS);

      const url = new URL(`${this.root}search/suggest`, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('resources[type]', 'product,collection,query');
      url.searchParams.set('resources[limit]', this.dataset.productsLimit || '10');
      url.searchParams.set('resources[limit_scope]', 'each');

      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Search suggestions returned ${response.status}`);
        const html = this.extract(await response.text());
        if (!html) throw new Error('Search suggestions came back without the panel');
        this.cache.set(key, html);
        if (this.query().toLowerCase() === key) this.render(html);
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (this.query().toLowerCase() === key) this.showError();
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          window.clearTimeout(this.slowTimer);
        }
      }
    }

    // Stops any pending or in-flight search, leaving what is on screen.
    cancel() {
      window.clearTimeout(this.timer);
      window.clearTimeout(this.slowTimer);
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
      this.results.removeAttribute('aria-busy');
      if (this.dataset.state === 'loading') {
        this.dataset.state = this.results.hidden ? 'idle' : 'results';
      }
    }

    extract(text) {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const panel = doc.querySelector('[data-predictive-search]');
      return panel ? panel.outerHTML : '';
    }

    // The markup is Shopify's own render of our section, with every value
    // escaped by Liquid, so it can go into the page as it is.
    render(html) {
      this.results.innerHTML = html;
      const panel = this.results.firstElementChild;
      this.idle.hidden = true;
      if (this.error) this.error.hidden = true;
      this.results.hidden = false;
      this.results.removeAttribute('aria-busy');
      this.dataset.state = panel?.dataset.state === 'empty' ? 'empty' : 'results';
      const status = panel?.querySelector('[data-predictive-status]');
      if (this.status) this.status.textContent = status ? status.textContent.trim() : '';
    }

    showIdle() {
      this.cancel();
      this.results.hidden = true;
      if (this.error) this.error.hidden = true;
      this.idle.hidden = false;
      this.dataset.state = 'idle';
      if (this.status) this.status.textContent = '';
    }

    showError() {
      this.results.hidden = true;
      this.results.removeAttribute('aria-busy');
      this.idle.hidden = true;
      this.dataset.state = 'error';
      if (this.error) {
        this.error.hidden = false;
        if (this.status) this.status.textContent = this.error.querySelector('.search-empty__title')?.textContent.trim() || '';
      }
    }

    // The products shown before typing. Fetched once, on first open, so no
    // other page view pays for them. They are optional: if they fail, the
    // popular searches above still work, and the next opening tries again.
    async loadFeatured() {
      if (!this.featured || this.featuredState !== 'none') return;
      this.featuredState = 'loading';
      try {
        const url = new URL(`${this.root}search`, window.location.origin);
        url.searchParams.set('section_id', 'predictive-search');
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Search panel products returned ${response.status}`);
        const html = this.extract(await response.text());
        this.featured.innerHTML = html;
        this.featuredState = 'done';
      } catch {
        this.featuredState = 'none';
      }
    }

    /* --- Recent searches ---------------------------------------------------
     * Kept only in this browser, for this shopper's convenience. Storage can
     * be unavailable (private windows, blocked site data), so every access
     * is guarded and the panel works the same without it. */

    readRecent() {
      try {
        const list = JSON.parse(window.localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
        return Array.isArray(list) ? list.filter((term) => typeof term === 'string').slice(0, RECENT_SEARCHES_MAX) : [];
      } catch {
        return [];
      }
    }

    remember(term) {
      if (!('showRecent' in this.dataset)) return;
      const clean = (term || '').trim();
      if (clean.length < SEARCH_MIN_CHARS) return;
      const lower = clean.toLowerCase();
      const list = [clean, ...this.readRecent().filter((t) => t.toLowerCase() !== lower)].slice(0, RECENT_SEARCHES_MAX);
      try {
        window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(list));
      } catch {
        // Storage is unavailable; the search itself is unaffected.
      }
    }

    searchUrl(term) {
      const url = new URL(this.form.action, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      return url.toString();
    }

    renderRecent() {
      if (!this.recent) return;
      const list = 'showRecent' in this.dataset ? this.readRecent() : [];
      const target = this.recent.querySelector('[data-recent-list]');
      target.replaceChildren(
        ...list.map((term) => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.className = 'pill';
          link.href = this.searchUrl(term);
          link.dataset.searchTerm = term;
          link.textContent = term;
          item.append(link);
          return item;
        })
      );
      this.recent.hidden = list.length === 0;
    }

    clearRecent() {
      try {
        window.localStorage.removeItem(RECENT_SEARCHES_KEY);
      } catch {
        // Nothing stored, nothing to clear.
      }
      this.renderRecent();
      this.input.focus();
    }

    onClick(event) {
      if (event.target.closest('[data-recent-clear]')) {
        this.clearRecent();
        return;
      }
      const link = event.target.closest('a[href]');
      if (!link) return;
      // A suggestion or recent search names its own term; any other result
      // was reached through what is in the input.
      if (link.dataset.searchTerm) {
        this.remember(link.dataset.searchTerm);
      } else if (this.results.contains(link)) {
        this.remember(this.query());
      }
    }

    // Down from the input moves into the results; Up and Down then step
    // through every link and button in the panel, and Up from the first one
    // returns to the input. Tab still works as usual.
    onKeydown(event) {
      // A search field uses Escape to clear its text, which would make closing
      // the panel take two presses. The query is kept for when it reopens, so
      // closing on the first press loses nothing.
      if (event.key === 'Escape' && event.target === this.input && this.input.value) {
        event.preventDefault();
        this.dialog.close();
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

      const items = [...this.querySelectorAll('.search-sheet__body a[href], .search-sheet__body button')].filter(
        (el) => !el.closest('[hidden]') && el.getClientRects().length > 0
      );

      if (event.target === this.input) {
        if (event.key === 'ArrowDown' && items.length > 0) {
          event.preventDefault();
          items[0].focus();
        }
        return;
      }

      const index = items.indexOf(event.target);
      if (index === -1) return;
      event.preventDefault();
      if (event.key === 'ArrowDown') {
        items[Math.min(index + 1, items.length - 1)].focus();
      } else if (index === 0) {
        this.input.focus();
      } else {
        items[index - 1].focus();
      }
    }
  }

  if (!customElements.get('search-sheet')) customElements.define('search-sheet', SearchSheet);
  if (!customElements.get('nav-dropdown')) customElements.define('nav-dropdown', NavDropdown);
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);
  if (!customElements.get('scroll-row')) customElements.define('scroll-row', ScrollRow);
})();
